import {
  sanitizeBoolString,
  sanitizeDate,
  sanitizeMaskedDate,
  sanitizeMaskedNumString,
  sanitizeMaskedString,
  sanitizeNum,
  sanitizeString,
} from '../utils';
import { ClientRequestType, ServerDriversRequestType } from './types';
import { transformMilitaryService } from './transformMilitaryService';

export const transformSaveDriversRequest = (
  data: ClientRequestType
): ServerDriversRequestType => {
  const {
    values: driverData,
    initialValues,
    editableFields,
    maskedFields,
  } = data;
  const insurableParties = Object.keys(driverData).map((driverId) => {
    const driver = driverData[driverId];
    const initialDriverData = initialValues[driverId];
    const driverEditability = editableFields[driverId];
    const driverMaskedFields = maskedFields[driverId];

    return {
      identifier: driverId,
      coverageHistory: {
        coveredWithinLookbackPeriod: sanitizeString(
          driver.coveredWithinLookbackPeriod,
          driverEditability.coveredWithinLookbackPeriod
        ),
      },
      personalInfo: {
        firstName: sanitizeString(
          driver.firstName,
          driverEditability.firstName
        ),
        middleName: sanitizeString(
          driver.middleName,
          driverEditability.middleName
        ),
        lastName: sanitizeString(driver.lastName, driverEditability.lastName),
        suffix: sanitizeString(driver.suffix, driverEditability.suffix),
        dateOfBirth: sanitizeMaskedDate(
          driver.dateOfBirth,
          driverEditability.dateOfBirth
        ),
        maritalStatus: sanitizeString(
          driver.maritalStatus,
          driverEditability.maritalStatus
        ),
        relationshipToApplicant: sanitizeString(
          driver.relationshipToApplicant,
          driverEditability.relationshipToApplicant
        ),
        gender: sanitizeString(driver.gender, driverEditability.gender),
        ssn: sanitizeMaskedNumString(driver.ssn, driverEditability.ssn),
        declinedSsn: driver.declinedSsn,
      },
      education: {
        currentLevel: sanitizeString(
          driver.educationLevel,
          driverEditability.educationLevel
        ),
      },
      excludeFromQuote: sanitizeBoolString(
        driver.excludeFromQuote,
        driverEditability.excludeFromQuote
      ),
      exclusionReason: sanitizeString(
        driver.exclusionReason,
        driverEditability.exclusionReason
      ),
      profession: sanitizeString(
        driver.profession,
        driverEditability.profession
      ),
      homeOwnership: {
        homeOwner: sanitizeBoolString(
          driver.isHomeOwner,
          driverEditability.isHomeOwner
        ),
      },
      militaryService: transformMilitaryService(driver, driverEditability),
      driverLicense: {
        number: sanitizeMaskedString(
          driver.licenseNumber,
          initialDriverData.licenseNumber,
          driverEditability.licenseNumber,
          driverMaskedFields.licenseNumber
        ),
        state: sanitizeString(
          driver.licenseState,
          driverEditability.licenseState
        ),
        status: sanitizeString(
          driver.licenseStatus,
          driverEditability.licenseStatus
        ),
        hasForeignLicense: sanitizeBoolString(
          driver.hasForeignLicense,
          driverEditability.hasForeignLicense
        ),
        hasLearnersPermit: sanitizeBoolString(
          driver.hasLearnersPermit,
          driverEditability.hasLearnersPermit
        ),
        userReportedStatus: sanitizeString(
          driver.userReportedStatus,
          driverEditability.userReportedStatus
        ),
      },
      stateResidency: {
        inStateResidencyDuration: sanitizeString(
          driver.inStateResidencyDuration,
          driverEditability.inStateResidencyDuration
        ),
        multipleHouseholdsMultipleStates: sanitizeBoolString(
          driver.multipleHouseholdsMultipleStates,
          driverEditability.multipleHouseholdsMultipleStates
        ),
        outOfStateStudent: sanitizeBoolString(
          driver.outOfStateStudent,
          driverEditability.outOfStateStudent
        ),
        ownVehicleWhileOutOfStateStudent: sanitizeBoolString(
          driver.ownVehicleWhileOutOfStateStudent,
          driverEditability.ownVehicleWhileOutOfStateStudent
        ),
      },
      statusDetails: {
        livesInHousehold: sanitizeBoolString(
          driver.livesInHousehold,
          driverEditability.livesInHousehold
        ),
        hasAccessToVehicles: sanitizeBoolString(
          driver.hasAccessToVehicles,
          driverEditability.hasAccessToVehicles
        ),
        hasOwnInsurancePolicy: sanitizeBoolString(
          driver.hasOwnInsurancePolicy,
          driverEditability.hasOwnInsurancePolicy
        ),
      },
      isGoodStudent: sanitizeBoolString(
        driver.isGoodStudent,
        driverEditability.isGoodStudent
      ),
      currentInsurance: driverEditability.isCurrentlyInsured
        ? driver.isCurrentlyInsured === 'true'
          ? {
              insured: true,
              carrierName: sanitizeString(
                driver.currentInsuranceCarrierName,
                driverEditability.currentInsuranceCarrierName
              ),
              biLevel: sanitizeString(
                driver.currentInsuranceBiLevel,
                driverEditability.currentInsuranceBiLevel
              ),
              lapseReason: null,
              lapseReasonOther: null,
              lapseDuration: null,
            }
          : driver.isCurrentlyInsured === 'false'
            ? {
                insured: false,
                carrierName: null,
                biLevel: null,
                lapseReason: sanitizeString(
                  driver.currentInsuranceLapseReason,
                  driverEditability.currentInsuranceLapseReason
                ),
                lapseReasonOther: sanitizeString(
                  driver.currentInsuranceLapseReasonOther,
                  driverEditability.currentInsuranceLapseReasonOther
                ),
                lapseDuration: sanitizeString(
                  driver.currentInsuranceLapseDuration,
                  driverEditability.currentInsuranceLapseDuration
                ),
              }
            : {
                insured: null,
                carrierName: null,
                biLevel: null,
                lapseReason: null,
                lapseReasonOther: null,
                lapseDuration: null,
              }
        : undefined,
      drivingHistory: {
        ageFirstLicensed: driverEditability.ageFirstLicensed
          ? sanitizeNum(driver.ageFirstLicensed)
          : undefined,
        ...(driverEditability.licensePreviouslySuspended
          ? driver.licensePreviouslySuspended === 'true'
            ? {
                licensePreviouslySuspended: true,
                ...(driverEditability.previousSuspensionDate
                  ? driver.previousSuspensionDate === 'five_years_or_less'
                    ? {
                        previousSuspensionCount: sanitizeString(
                          driver.previousSuspensionCount,
                          driverEditability.previousSuspensionCount
                        ),
                        previousSuspensionDate: sanitizeString(
                          driver.previousSuspensionDate,
                          driverEditability.previousSuspensionDate
                        ),
                      }
                    : {
                        previousSuspensionCount: null,
                        previousSuspensionDate: sanitizeString(
                          driver.previousSuspensionDate,
                          driverEditability.previousSuspensionDate
                        ),
                      }
                  : {}),
              }
            : driver.licensePreviouslySuspended === 'false'
              ? {
                  licensePreviouslySuspended: false,
                  previousSuspensionCount: null,
                  previousSuspensionDate: null,
                }
              : {
                  licensePreviouslySuspended: null,
                  previousSuspensionCount: null,
                  previousSuspensionDate: null,
                }
          : {}),
      },
      drivingCourse: driverEditability.hasCompletedDrivingCourse
        ? driver.hasCompletedDrivingCourse === 'true'
          ? {
              completionDate: sanitizeDate(
                driver.drivingCourseDate,
                driverEditability.drivingCourseDate
              ),
              courseTaken: true,
            }
          : driver.hasCompletedDrivingCourse === 'false'
            ? {
                completionDate: null,
                courseTaken: false,
              }
            : {
                completionDate: null,
                courseTaken: null,
              }
        : undefined,
      accidents: Object.keys(driver.accidents).map((key) => {
        return {
          identifier: key,
          atFault: !!sanitizeBoolString(driver.accidents[key].atFault),
          lossType: sanitizeString(driver.accidents[key].lossType),
          accidentType: sanitizeString(driver.accidents[key].accidentType),
          day: sanitizeNum(driver.accidents[key].day),
          month: sanitizeString(driver.accidents[key].month),
          year: sanitizeNum(driver.accidents[key].year),
          typeOfLoss: sanitizeString(driver.accidents[key].typeOfLoss),
        };
      }),
      violations: Object.keys(driver.violations).map((key) => {
        return {
          identifier: key,
          month: sanitizeString(driver.violations[key].month),
          day: sanitizeNum(driver.violations[key].day),
          year: sanitizeNum(driver.violations[key].year),
          violationType: sanitizeString(driver.violations[key].violationType),
          sr22: sanitizeBoolString(driver.violations[key].sr22),
        };
      }),
      cfr: {
        required: sanitizeBoolString(
          driver.cfrRequired,
          driverEditability.cfrRequired
        ),
        caseNumber:
          driver.cfrRequired !== 'false'
            ? sanitizeString(
                driver.cfrCaseNumber,
                driverEditability.cfrCaseNumber
              )
            : null,
        cfrType:
          driver.cfrRequired !== 'false'
            ? sanitizeString(driver.cfrType, driverEditability.cfrType)
            : null,
        issueState:
          driver.cfrRequired !== 'false'
            ? sanitizeString(
                driver.cfrIssueState,
                driverEditability.cfrIssueState
              )
            : null,
      },
    };
  });

  return {
    insurableParties,
  };
};
